// 200 retro-aesthetic presets for the Studio dotgrad layer. Each preset is a
// fully-resolved layer config (shape, stops, bg, cell, gap, spread plus
// optional overrides) so picking one snaps the entire scene to that look.

import type { Stop } from './swatches';

export type RetroPreset = {
  id: string;
  name: string;
  shape: string;
  stops: readonly Stop[];
  bg: string;
  cell: number;
  gap: number;
  spread: number;
  angle?: number;
  offset?: number;
  repeat?: number;
  centerX?: number;
  centerY?: number;
  mirror?: boolean;
  oklab?: boolean;
  noise?: number;
  quant?: number;
};

export type RetroGroup = { label: string; presets: readonly RetroPreset[] };

const p = (
  id: string, name: string, shape: string,
  stops: readonly Stop[], bg: string,
  cell: number, gap: number, spread: number,
  extras: Partial<RetroPreset> = {},
): RetroPreset => ({ id, name, shape, stops, bg, cell, gap, spread, ...extras });

// ---- Shared palettes ------------------------------------------------------

const SYNTHWAVE: Stop[] = [
  { t: 0, color: '#1a0033' }, { t: 0.4, color: '#ff10f0' },
  { t: 0.7, color: '#ffa500' }, { t: 1, color: '#00f5ff' },
];
const VAPORWAVE: Stop[] = [
  { t: 0, color: '#ff80ab' }, { t: 0.5, color: '#ce93d8' }, { t: 1, color: '#80deea' },
];
const ARCADE: Stop[] = [
  { t: 0, color: '#ff0000' }, { t: 0.25, color: '#ffff00' }, { t: 0.5, color: '#00ff00' },
  { t: 0.75, color: '#00ffff' }, { t: 1, color: '#ff00ff' },
];
const CRT_GREEN: Stop[] = [
  { t: 0, color: '#001100' }, { t: 0.5, color: '#00ff41' }, { t: 1, color: '#aaffaa' },
];
const CRT_AMBER: Stop[] = [
  { t: 0, color: '#1a0a00' }, { t: 0.5, color: '#ffaa00' }, { t: 1, color: '#ffe6b3' },
];
const NEON_MAGENTA: Stop[] = [{ t: 0, color: '#ff00ff' }, { t: 1, color: '#1a001a' }];
const NEON_CYAN: Stop[] = [{ t: 0, color: '#00ffff' }, { t: 1, color: '#001a1a' }];
const NEON_BIPOLE: Stop[] = [
  { t: 0, color: '#0a0014' }, { t: 0.5, color: '#ff10f0' }, { t: 1, color: '#00ffff' },
];
const NES: Stop[] = [
  { t: 0, color: '#000080' }, { t: 0.25, color: '#f80058' }, { t: 0.5, color: '#f87858' },
  { t: 0.75, color: '#f8d878' }, { t: 1, color: '#00b8f8' },
];
const SNES: Stop[] = [
  { t: 0, color: '#1a1a4a' }, { t: 0.3, color: '#ff8000' },
  { t: 0.6, color: '#ffff80' }, { t: 1, color: '#80ffff' },
];
const ATARI: Stop[] = [
  { t: 0, color: '#404040' }, { t: 0.5, color: '#88c070' }, { t: 1, color: '#d0a060' },
];
const GAMEBOY: Stop[] = [
  { t: 0, color: '#0f380f' }, { t: 0.33, color: '#306230' },
  { t: 0.66, color: '#8bac0f' }, { t: 1, color: '#9bbc0f' },
];
const DISCO: Stop[] = [
  { t: 0, color: '#1a0033' }, { t: 0.5, color: '#ff1493' }, { t: 1, color: '#ffd700' },
];
const POPART: Stop[] = [
  { t: 0, color: '#fc1cb1' }, { t: 0.5, color: '#ffd831' }, { t: 1, color: '#000000' },
];
const OPART: Stop[] = [{ t: 0, color: '#000000' }, { t: 1, color: '#ffffff' }];
const ARTDECO: Stop[] = [
  { t: 0, color: '#1a1a1a' }, { t: 0.5, color: '#d4af37' }, { t: 1, color: '#f5e6d3' },
];
const PSYCH: Stop[] = [
  { t: 0, color: '#ff0000' }, { t: 0.2, color: '#ff7f00' }, { t: 0.4, color: '#ffff00' },
  { t: 0.6, color: '#00ff00' }, { t: 0.8, color: '#0000ff' }, { t: 1, color: '#8b00ff' },
];
const PUNK: Stop[] = [
  { t: 0, color: '#000000' }, { t: 0.5, color: '#ff0000' }, { t: 1, color: '#ffffff' },
];
const MEMPHIS: Stop[] = [
  { t: 0, color: '#ff5a8a' }, { t: 0.33, color: '#00b4d8' },
  { t: 0.66, color: '#f0c020' }, { t: 1, color: '#1a1a1a' },
];
const DYSTOPIAN: Stop[] = [
  { t: 0, color: '#0a0a0a' }, { t: 0.5, color: '#39ff14' }, { t: 1, color: '#ff10f0' },
];
const Y2K: Stop[] = [
  { t: 0, color: '#a8c8e8' }, { t: 0.5, color: '#e8e8f8' }, { t: 1, color: '#d8b878' },
];
const DEMOSCENE: Stop[] = [
  { t: 0, color: '#000010' }, { t: 0.4, color: '#a818a8' },
  { t: 0.75, color: '#1a78ff' }, { t: 1, color: '#a8e0ff' },
];
const ZINE: Stop[] = [
  { t: 0, color: '#1a1a1a' }, { t: 0.5, color: '#c0583a' }, { t: 1, color: '#f5e6d3' },
];

// ---- Presets (10 per group × 20 groups = 200) -----------------------------

export const RETRO_PRESETS: RetroGroup[] = [
  { label: 'Synthwave', presets: [
    p('sw-sunset',     'Sunset Drive',     'linear',         SYNTHWAVE, '#1a0033',  6,  0, 480, { angle: 90 }),
    p('sw-grid',       'Neon Grid',        'checker',        SYNTHWAVE, '#000000', 16,  0, 200),
    p('sw-mountains',  'Mountain Ridge',   'triangle-wave',  SYNTHWAVE, '#1a0033',  8,  0, 300, { angle: 90 }),
    p('sw-laser',      'Laser Floor',      'stripes-d',      SYNTHWAVE, '#000000',  4,  0, 100),
    p('sw-horizon',    'Horizon Sun',      'sine-radial',    SYNTHWAVE, '#1a0033',  4,  0, 240),
    p('sw-tron',       'Tron Lattice',     'weave',          NEON_BIPOLE, '#000000', 16,  0, 200),
    p('sw-vortex',     'Highway Vortex',   'spiral',         SYNTHWAVE, '#1a0033',  6,  0, 800),
    p('sw-eclipse',    'Magenta Eclipse',  'radial',         SYNTHWAVE, '#000000',  4,  0, 1500),
    p('sw-runway',     'Runway Lights',    'stripes-v',      SYNTHWAVE, '#1a0033',  4,  0, 80),
    p('sw-static',     'Synth Static',     'fbm',            SYNTHWAVE, '#1a0033',  4,  0, 200),
  ]},
  { label: 'Vaporwave', presets: [
    p('vw-clouds',     'Pastel Clouds',    'fbm',            VAPORWAVE, '#fff0fa',  4,  0, 600),
    p('vw-lo-fi',      'Lo-Fi Haze',       'sine-radial',    VAPORWAVE, '#fff0fa',  4,  0, 800),
    p('vw-grid',       'Pastel Grid',      'checker',        VAPORWAVE, '#fff0fa', 12,  0, 200),
    p('vw-statue',     'Marble Statue',    'radial',         VAPORWAVE, '#fff0fa',  4,  0, 1200),
    p('vw-glitch',     'Glitched Frame',   'tear',           VAPORWAVE, '#fff0fa',  4,  0, 400),
    p('vw-bubble',     'Bubble Loft',      'radial-bubble',  VAPORWAVE, '#fff0fa',  4,  0, 700),
    p('vw-tropics',    'Tropic Pastel',    'sine-y',         VAPORWAVE, '#fff0fa',  4,  0, 200),
    p('vw-aesthetic',  'Aesthetic Dots',   'dots-grid',      VAPORWAVE, '#fff0fa',  4,  0, 480),
    p('vw-shrine',     'Greek Shrine',     'poly-6',         VAPORWAVE, '#fff0fa',  4,  0, 800),
    p('vw-sunrise',    'Pink Sunrise',     'linear',         VAPORWAVE, '#fff0fa',  4,  0, 800, { angle: 90 }),
  ]},
  { label: 'Arcade', presets: [
    p('ar-pacman',     'Pac-Man',          'dots-grid',      ARCADE, '#000000',  4,  0, 240),
    p('ar-tetris',     'Tetris Block',     'stripes-v',      ARCADE, '#000000', 16,  0, 80),
    p('ar-invader',    'Space Invader',    'checker',        ARCADE, '#000000',  8,  0, 80),
    p('ar-asteroid',   'Asteroid Field',   'radial',         OPART,  '#000000',  4,  0, 800),
    p('ar-target',     'Target Lock',      'ripple',         ARCADE, '#000000',  4,  0, 600),
    p('ar-coin',       'Power Coin',       'poly-8',         ARCADE, '#000000',  4,  0, 400),
    p('ar-powerup',    'Power-Up',         'rose-5',         ARCADE, '#000000',  4,  0, 300),
    p('ar-tube',       'Tube Wave',        'sine',           ARCADE, '#000000',  4,  0, 200),
    p('ar-laser',      'Laser Beam',       'stripes-d',      ARCADE, '#000000',  4,  0, 60),
    p('ar-block',      'Coin Block',       'brick',          ARCADE, '#000000',  6,  0, 240),
  ]},
  { label: 'CRT Phosphor', presets: [
    p('crt-monitor',   'Old Monitor',      'stripes-h',      CRT_GREEN, '#001100',  4,  0, 30),
    p('crt-radar',     'Radar Sweep',      'radial',         CRT_GREEN, '#001100',  4,  0, 800),
    p('crt-glow',      'Phosphor Glow',    'radial-bubble',  CRT_GREEN, '#001100',  4,  0, 500),
    p('crt-scanline',  'Scanline',         'stripes-h',      CRT_GREEN, '#001100',  2,  0, 12),
    p('crt-amber',     'Amber Terminal',   'linear',         CRT_AMBER, '#1a0a00',  4,  0, 600, { angle: 90 }),
    p('crt-trinitron', 'Trinitron Mask',   'stripes-v',      CRT_GREEN, '#001100',  2,  0, 8),
    p('crt-burn',      'Screen Burn',      'ripple',         CRT_GREEN, '#001100',  4,  0, 600),
    p('crt-noise',     'CRT Noise',        'fbm',            CRT_GREEN, '#001100',  4,  0, 300, { noise: 30 }),
    p('crt-vector',    'Vector Pulse',     'rose-5',         CRT_GREEN, '#001100',  4,  0, 500),
    p('crt-terminal',  'Terminal Dots',    'dots-grid',      CRT_GREEN, '#001100',  4,  0, 320),
  ]},
  { label: 'Neon Signs', presets: [
    p('ne-loop',       'Neon Loop',        'ripple',         NEON_MAGENTA, '#0a0014', 4,  0, 500),
    p('ne-cursor',     'Open Sign',        'stripes-v',      NEON_CYAN,    '#0a0014', 4,  0, 60),
    p('ne-wires',      'Neon Wires',       'spiral',         NEON_BIPOLE,  '#0a0014', 6,  0, 700),
    p('ne-cocktail',   'Cocktail Sign',    'radial',         NEON_MAGENTA, '#0a0014', 4,  0, 1000),
    p('ne-arrow',      'Arrow Sign',       'linear',         NEON_CYAN,    '#0a0014', 4,  0, 480, { angle: 45 }),
    p('ne-heart',      'Heart Sign',       'heart',          NEON_MAGENTA, '#0a0014', 4,  0, 200),
    p('ne-star',       'Neon Star',        'star-5',         NEON_CYAN,    '#0a0014', 4,  0, 300),
    p('ne-grid',       'Sign Grid',        'checker',        NEON_BIPOLE,  '#0a0014', 12, 0, 240),
    p('ne-buzz',       'Buzzing Tube',     'sine',           NEON_MAGENTA, '#0a0014', 4,  0, 100),
    p('ne-dotsign',    'Dot Sign',         'dots-grid',      NEON_BIPOLE,  '#0a0014', 4,  0, 320),
  ]},
  { label: '8-bit NES', presets: [
    p('nes-sky',       'Mario Sky',        'linear',         NES, '#000080',  6,  0, 600, { angle: 90 }),
    p('nes-castle',    'Bowser Castle',    'checker',        NES, '#000000', 12,  0, 200),
    p('nes-pipe',      'Warp Pipe',        'stripes-v',      NES, '#000000',  8,  0, 100),
    p('nes-coin',      'Gold Coin',        'poly-6',         NES, '#000080',  4,  0, 300),
    p('nes-bowser',    'Castle Stairs',    'brick',          NES, '#000000',  8,  0, 240),
    p('nes-zelda',     'Hyrule Field',     'dots-grid',      NES, '#00b8f8',  4,  0, 240),
    p('nes-mountain',  '8-bit Mountain',   'triangle-grid',  NES, '#000080',  6,  0, 240),
    p('nes-water',     'Pixel Water',      'stripes-h',      NES, '#000080',  4,  0, 60),
    p('nes-fire',      'Pixel Fire',       'ripple',         NES, '#000000',  4,  0, 400),
    p('nes-cave',      'Cave Floor',       'radial',         NES, '#000000',  4,  0, 1000),
  ]},
  { label: '16-bit SNES', presets: [
    p('snes-mode7',    'Mode-7 Plain',     'linear',         SNES, '#1a1a4a',  4,  0, 800, { angle: 90 }),
    p('snes-fz',       'F-Zero Track',     'checker',        SNES, '#1a1a4a',  8,  0, 240),
    p('snes-mana',     'Sword of Mana',    'radial',         SNES, '#1a1a4a',  4,  0, 1200),
    p('snes-yoshi',    'Yoshi Island',     'dots-grid',      SNES, '#80ffff',  4,  0, 360),
    p('snes-rpg',      'Hex Town',         'hex',            SNES, '#1a1a4a',  4,  0, 240),
    p('snes-link',     'Hyrule 16',        'stripes-d',      SNES, '#1a1a4a',  4,  0, 100),
    p('snes-dkc',      'DKC Jungle',       'fbm',            SNES, '#1a1a4a',  4,  0, 600),
    p('snes-cv',       'Castlevania',      'linear',         SNES, '#000000',  4,  0, 1200, { angle: 0 }),
    p('snes-sf',       'Street Fighter',   'stripes-v',      SNES, '#1a1a4a',  4,  0, 80),
    p('snes-zoom',     'Mode-7 Zoom',      'spiral',         SNES, '#1a1a4a',  4,  0, 800),
  ]},
  { label: 'Atari 2600', presets: [
    p('at-pong',       'Pong Court',       'stripes-v',      OPART,  '#000000',  4,  0, 60),
    p('at-asteroids',  'Asteroids Field',  'value-noise',    OPART,  '#000000',  4,  0, 400),
    p('at-invader',    'Atari Invader',    'checker',        ATARI,  '#000000',  6,  0, 80),
    p('at-frogger',    'Frogger Lanes',    'stripes-h',      ATARI,  '#404040',  6,  0, 80),
    p('at-pacman',     'Atari Pac',        'dots-grid',      ATARI,  '#404040',  4,  0, 240),
    p('at-pitfall',    'Jungle Pitfall',   'linear',         ATARI,  '#404040',  4,  0, 600, { angle: 90 }),
    p('at-defender',   'Defender Sky',     'stripes-v',      ATARI,  '#000000',  4,  0, 100),
    p('at-galaga',     'Galaga Stars',     'poly-5',         ATARI,  '#000000',  4,  0, 300),
    p('at-centipede',  'Centipede Path',   'weave',          ATARI,  '#404040',  6,  0, 200),
    p('at-yarns',      'Yarns Spiral',     'spiral',         ATARI,  '#404040',  4,  0, 600),
  ]},
  { label: 'Game Boy', presets: [
    p('gb-tetris',     'GB Tetris',        'stripes-v',      GAMEBOY, '#9bbc0f',  6,  0, 100),
    p('gb-pokemon',    'Pokémon Town',     'hex',            GAMEBOY, '#9bbc0f',  4,  0, 240),
    p('gb-zelda',      'Link Awakening',   'radial',         GAMEBOY, '#9bbc0f',  4,  0, 800),
    p('gb-screen',     'GB Screen',        'stripes-h',      GAMEBOY, '#9bbc0f',  4,  0, 30),
    p('gb-pixels',     'GB Pixels',        'checker',        GAMEBOY, '#9bbc0f',  4,  0, 80),
    p('gb-grid',       'Dot Matrix',       'dots-grid',      GAMEBOY, '#9bbc0f',  4,  0, 240),
    p('gb-mario',      'Mario Land',       'linear',         GAMEBOY, '#9bbc0f',  4,  0, 600, { angle: 90 }),
    p('gb-kirby',      'Kirby Bubble',     'poly-6',         GAMEBOY, '#9bbc0f',  4,  0, 360),
    p('gb-metroid',    'Metroid Cave',     'ripple',         GAMEBOY, '#0f380f',  4,  0, 600),
    p('gb-fade',       'GB Fade',          'sine-radial',    GAMEBOY, '#9bbc0f',  4,  0, 1000),
  ]},
  { label: 'Disco 70s', presets: [
    p('di-mirrorball', 'Mirrorball',       'radial',         DISCO, '#1a0033',  4,  0, 600),
    p('di-platform',   'Platform Floor',   'checker',        DISCO, '#000000', 12,  0, 200),
    p('di-velvet',     'Velvet Drape',     'linear',         DISCO, '#1a0033',  6,  0, 800, { angle: 90 }),
    p('di-funk',       'Funk Spiral',      'spiral',         DISCO, '#1a0033',  4,  0, 800),
    p('di-soul',       'Soul Ripple',      'ripple',         DISCO, '#1a0033',  4,  0, 700),
    p('di-saturday',   'Saturday Night',   'stripes-d',      DISCO, '#1a0033',  4,  0, 80),
    p('di-glam',       'Glam Bloom',       'rose-5',         DISCO, '#1a0033',  4,  0, 400),
    p('di-disco-dot',  'Disco Dots',       'dots-grid',      DISCO, '#1a0033',  4,  0, 280),
    p('di-floor',      'Hex Dance Floor',  'hex',            DISCO, '#000000',  6,  0, 200),
    p('di-vinyl',      'Vinyl Spin',       'conic-12',       DISCO, '#000000',  4,  0, 100),
  ]},
  { label: 'Pop Art 60s', presets: [
    p('pa-lichten',    'Lichtenstein',     'dots-grid',      POPART, '#ffffff',  4,  0, 240),
    p('pa-warhol',     'Warhol Strip',     'stripes-v',      POPART, '#ffd831',  8,  0, 80),
    p('pa-banana',     'Banana Yellow',    'linear',         POPART, '#ffd831',  4,  0, 800, { angle: 90 }),
    p('pa-comic',      'Comic Panel',      'checker',        POPART, '#ffffff', 12,  0, 200),
    p('pa-bang',       'Bang!',            'radial',         POPART, '#ffd831',  4,  0, 800),
    p('pa-news',       'Newsprint',        'stripes-h',      OPART,  '#ffffff',  2,  0, 16),
    p('pa-soup',       'Soup Can',         'stripes-v',      POPART, '#ffffff',  4,  0, 60),
    p('pa-flower',     'Pop Flower',       'poly-6',         POPART, '#ffffff',  4,  0, 300),
    p('pa-zap',        'Zap Step',         'linear-step',    POPART, '#ffffff',  4,  0, 600, { angle: 45 }),
    p('pa-marilyn',    'Marilyn Tile',     'checker',        POPART, '#ff80ab',  8,  0, 320),
  ]},
  { label: 'Op Art', presets: [
    p('oa-riley',      'Riley Lines',      'stripes-v',      OPART, '#ffffff',  2,  0, 12),
    p('oa-vasarely',   'Vasarely',         'checker',        OPART, '#ffffff',  8,  0, 200),
    p('oa-vortex',     'Vortex',           'spiral',         OPART, '#ffffff',  4,  0, 800),
    p('oa-illusion',   'Illusion',         'ripple',         OPART, '#ffffff',  4,  0, 400),
    p('oa-zigzag',     'Zigzag',           'triangle-wave',  OPART, '#ffffff',  4,  0, 80, { angle: 90 }),
    p('oa-target',     'Target',           'radial',         OPART, '#ffffff',  4,  0, 200),
    p('oa-current',    'Current',          'sine',           OPART, '#ffffff',  4,  0, 120),
    p('oa-warp',       'Warp Field',       'log-spiral',     OPART, '#ffffff',  4,  0, 800),
    p('oa-bend',       'Bent Stripes',     'sine-x',         OPART, '#ffffff',  4,  0, 200),
    p('oa-grid-warp',  'Grid Warp',        'checker',        OPART, '#ffffff',  4,  0, 100, { angle: 30 }),
  ]},
  { label: 'Art Deco', presets: [
    p('ad-chrysler',   'Chrysler Crown',   'poly-8',         ARTDECO, '#1a1a1a',  4,  0, 400),
    p('ad-fan',        'Fan Bloom',        'rose-5',         ARTDECO, '#1a1a1a',  4,  0, 500),
    p('ad-rays',       'Sunray',           'conic-12',       ARTDECO, '#1a1a1a',  4,  0, 100),
    p('ad-zigzag',     'Zigzag Frieze',    'triangle-wave',  ARTDECO, '#1a1a1a',  4,  0, 100, { angle: 90 }),
    p('ad-stepped',    'Stepped Pyramid',  'linear-step',    ARTDECO, '#1a1a1a',  6,  0, 600, { angle: 90 }),
    p('ad-leaf',       'Gold Leaf',        'stripes-d',      ARTDECO, '#1a1a1a',  4,  0, 80),
    p('ad-empire',     'Empire Tower',     'stripes-v',      ARTDECO, '#1a1a1a',  6,  0, 80),
    p('ad-gatsby',     'Gatsby Bloom',     'poly-6',         ARTDECO, '#1a1a1a',  4,  0, 360),
    p('ad-jazz',       'Jazz Spiral',      'spiral',         ARTDECO, '#1a1a1a',  4,  0, 600),
    p('ad-mono',       'Cream Monolith',   'linear',         ARTDECO, '#1a1a1a',  4,  0, 1200, { angle: 90 }),
  ]},
  { label: 'Psychedelic', presets: [
    p('ps-tiedye',     'Tie-Dye',          'spiral',         PSYCH, '#000000',  4,  0, 800),
    p('ps-mandala',    'Mandala',          'mandala',        PSYCH, '#000000',  4,  0, 480),
    p('ps-flower',     'Flower Power',     'rose-5',         PSYCH, '#000000',  4,  0, 400),
    p('ps-warp',       'Warp Sun',         'conic',          PSYCH, '#000000',  4,  0, 200),
    p('ps-trip',       'Trip Ripple',      'ripple',         PSYCH, '#000000',  4,  0, 800),
    p('ps-vortex',     'Acid Vortex',      'log-spiral',     PSYCH, '#000000',  4,  0, 600),
    p('ps-fillmore',   'Fillmore Poster',  'poly-8',         PSYCH, '#000000',  4,  0, 400),
    p('ps-acid',       'Acid Drop',        'fbm',            PSYCH, '#000000',  4,  0, 600),
    p('ps-paisley',    'Paisley',          'sine-radial',    PSYCH, '#000000',  4,  0, 400),
    p('ps-rainbow',    'Rainbow Stripe',   'linear',         PSYCH, '#000000',  4,  0, 1000, { angle: 90 }),
  ]},
  { label: 'Punk 70s', presets: [
    p('pk-pin',        'Safety Pin',       'linear',         PUNK, '#000000',  4,  0, 600, { angle: 45 }),
    p('pk-rip',        'Ripped Page',      'tear',           PUNK, '#000000',  4,  0, 400),
    p('pk-anarchy',    'Anarchy Spiral',   'spiral',         PUNK, '#000000',  4,  0, 600),
    p('pk-stencil',    'Stencil',          'checker',        PUNK, '#000000', 12,  0, 200),
    p('pk-stage',      'Stage Stripes',    'stripes-d',      PUNK, '#000000',  4,  0, 100),
    p('pk-zine',       'Zine Halftone',    'dots-grid',      PUNK, '#ffffff',  4,  0, 200),
    p('pk-amp',        'Amp Buzz',         'stripes-h',      PUNK, '#000000',  2,  0, 12),
    p('pk-spike',      'Spike Pattern',    'triangle-wave',  PUNK, '#000000',  4,  0, 80, { angle: 90 }),
    p('pk-pin-grid',   'Pin Grid',         'dots-grid',      PUNK, '#000000',  4,  0, 240),
    p('pk-sneer',      'Sneer Step',       'linear-step',    PUNK, '#000000',  4,  0, 480),
  ]},
  { label: 'Memphis 80s', presets: [
    p('me-zigzag',     'Memphis Zigzag',   'triangle-wave',  MEMPHIS, '#fff5e0',  4,  0, 80, { angle: 90 }),
    p('me-confetti',   'Confetti',         'dots-grid',      MEMPHIS, '#fff5e0',  4,  0, 300),
    p('me-squiggle',   'Squiggle',         'spiral',         MEMPHIS, '#fff5e0',  4,  0, 600),
    p('me-stripes',    'Memphis Stripes',  'stripes-d',      MEMPHIS, '#fff5e0',  4,  0, 100),
    p('me-shapes',     'Shape Riot',       'poly-6',         MEMPHIS, '#fff5e0',  4,  0, 360),
    p('me-bubble',     'Bubble Drop',      'dots-grid',      MEMPHIS, '#fff5e0',  4,  0, 480),
    p('me-grid',       'Color Hex',        'hex',            MEMPHIS, '#fff5e0',  6,  0, 240),
    p('me-block',      'Block Stripes',    'stripes-v',      MEMPHIS, '#fff5e0',  6,  0, 80),
    p('me-rave',       'Memphis Rave',     'stripes-d',      MEMPHIS, '#fff5e0',  4,  0, 60),
    p('me-radial',     'Pastel Burst',     'radial',         MEMPHIS, '#fff5e0',  4,  0, 800),
  ]},
  { label: 'Dystopian 90s', presets: [
    p('dy-matrix',     'Matrix Rain',      'stripes-v',      CRT_GREEN, '#000000',  4,  0, 30),
    p('dy-glitch',     'Glitch Frame',     'tear',           DYSTOPIAN, '#000000',  4,  0, 400),
    p('dy-broken',     'Broken Signal',    'corrupt',        DYSTOPIAN, '#000000',  4,  0, 400),
    p('dy-static',     'Static Storm',     'value-noise',    DYSTOPIAN, '#000000',  4,  0, 200, { noise: 25 }),
    p('dy-hacker',     'Hacker Term',      'stripes-h',      CRT_GREEN, '#000000',  2,  0, 12),
    p('dy-cyber',      'Cyber Grid',       'linear',         DYSTOPIAN, '#000000',  4,  0, 200, { angle: 90 }),
    p('dy-circuit',    'Circuit Weave',    'weave',          CRT_GREEN, '#000000',  6,  0, 240),
    p('dy-virus',      'Virus Spiral',     'spiral',         DYSTOPIAN, '#000000',  4,  0, 600),
    p('dy-toxic',      'Toxic Ripple',     'ripple',         DYSTOPIAN, '#000000',  4,  0, 700),
    p('dy-error',      'Error 404',        'stripes-d',      DYSTOPIAN, '#000000',  4,  0, 100),
  ]},
  { label: 'Y2K', presets: [
    p('y2-chrome',     'Chrome Slick',     'linear',         Y2K, '#1a1a2e',  4,  0, 800, { angle: 90 }),
    p('y2-bubble',     'Bubble Lock',      'dots-grid',      Y2K, '#a8c8e8',  4,  0, 360),
    p('y2-flash',      'Flash Splash',     'ripple',         Y2K, '#a8c8e8',  4,  0, 600),
    p('y2-translucent','Translucent',      'linear-sqrt',    Y2K, '#a8c8e8',  4,  0, 1000),
    p('y2-frutiger',   'Frutiger Aero',    'sine-radial',    Y2K, '#a8c8e8',  4,  0, 700),
    p('y2-jelly',      'Jelly Bean',       'radial-bubble',  Y2K, '#a8c8e8',  4,  0, 500),
    p('y2-shimmer',    'Shimmer Spin',     'spiral',         Y2K, '#a8c8e8',  4,  0, 800),
    p('y2-glass',      'Glass Wave',       'cosine',         Y2K, '#a8c8e8',  4,  0, 200),
    p('y2-aurora',     'Aurora Glow',      'linear',         Y2K, '#1a1a2e',  4,  0, 1200),
    p('y2-cmyk',       'CMYK Strip',       'stripes-v',      Y2K, '#a8c8e8',  6,  0, 80),
  ]},
  { label: 'Demoscene', presets: [
    p('de-amiga',      'Amiga Copper',     'stripes-h',      DEMOSCENE, '#000010',  4,  0, 60),
    p('de-copper',     'Copper Bar',       'stripes-h',      DEMOSCENE, '#000010',  2,  0, 16),
    p('de-plasma',     'Plasma',           'plasma',         DEMOSCENE, '#000010',  4,  0, 600),
    p('de-tunnel',     'Tunnel',           'spiral',         DEMOSCENE, '#000010',  4,  0, 800),
    p('de-rotozoom',   'Rotozoom',         'log-spiral',     DEMOSCENE, '#000010',  4,  0, 600),
    p('de-checker',    'Demo Checker',     'checker',        DEMOSCENE, '#000010',  8,  0, 200, { angle: 30 }),
    p('de-fire',       'Fire Effect',      'ripple',         POPART,    '#000010',  4,  0, 600),
    p('de-vector',     'Vector Bloom',     'rose-5',         DEMOSCENE, '#000010',  4,  0, 500),
    p('de-cube',       '3-D Cube',         'linear-step',    DEMOSCENE, '#000010',  6,  0, 480, { angle: 60 }),
    p('de-scroll',     'Scroll Text',      'stripes-h',      DEMOSCENE, '#000010',  4,  0, 80),
  ]},
  { label: 'Retro Magazine', presets: [
    p('rm-halftone',   'Halftone',         'dots-grid',      OPART, '#ffffff',  4,  0, 200),
    p('rm-zine',       'Photocopy Zine',   'stripes-d',      ZINE,  '#f5e6d3',  4,  0, 100),
    p('rm-photocopy',  'Photocopy',        'value-noise',    OPART, '#ffffff',  4,  0, 200, { noise: 30 }),
    p('rm-newspaper',  'Newspaper',        'stripes-h',      OPART, '#ffffff',  2,  0, 24),
    p('rm-cutout',     'Paper Cutout',     'poly-6',         ZINE,  '#f5e6d3',  4,  0, 360),
    p('rm-collage',    'Hex Collage',      'hex',            ZINE,  '#f5e6d3',  6,  0, 240),
    p('rm-screen',     'Screen Tone',      'dots-grid',      ZINE,  '#f5e6d3',  4,  0, 300),
    p('rm-print',      'Print Stripe',     'stripes-v',      ZINE,  '#f5e6d3',  4,  0, 60),
    p('rm-fold',       'Fold Cover',       'linear',         ZINE,  '#f5e6d3',  4,  0, 800, { angle: 45 }),
    p('rm-comic',      'Comic Tone',       'dots-grid',      POPART,'#ffffff',  4,  0, 240),
  ]},
];

// Flat lookup map keyed by id.
const PRESET_BY_ID = new Map<string, RetroPreset>();
for (const g of RETRO_PRESETS) for (const pr of g.presets) PRESET_BY_ID.set(pr.id, pr);

export function getRetroPreset(id: string): RetroPreset | undefined {
  return PRESET_BY_ID.get(id);
}

export function retroPresetCount(): number {
  return PRESET_BY_ID.size;
}
